import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BuyerStrategyPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    buying_purpose: '',
    budget: '',
    loan_type: '',
    emi_range: '',
    possession_timeline: '',
    holding_period: '',
    risk_appetite: '',
    family_size: '',
    required_bhk: '',
    need_school_metro: false,
    rental_priority: false,
    appreciation_priority: false,
    open_to_under_construction: false,
    open_to_pre_launch: false,
    biggest_concern: ''
  });

  const steps = [
    { title: "Buying Purpose", key: "buying_purpose" },
    { title: "Budget", key: "budget" },
    { title: "Financing", key: "loan_type" },
    { title: "Possession Timeline", key: "possession_timeline" },
    { title: "Holding Period", key: "holding_period" },
    { title: "Risk Appetite", key: "risk_appetite" },
    { title: "Family Size", key: "family_size", conditional: true },
    { title: "Required BHK", key: "required_bhk", conditional: true },
    { title: "School/Metro", key: "need_school_metro", conditional: true },
    { title: "Rental Priority", key: "rental_priority", conditional: true },
    { title: "Appreciation Priority", key: "appreciation_priority", conditional: true },
    { title: "Under Construction", key: "open_to_under_construction", conditional: true },
    { title: "Pre Launch", key: "open_to_pre_launch", conditional: true },
    { title: "Biggest Concern", key: "biggest_concern" }
  ];

  const shouldShowFamilySteps = formData.buying_purpose === 'self_use';
  const shouldShowInvestmentSteps = ['investment', 'rental', 'flipping'].includes(formData.buying_purpose);

  const handleNext = async () => {
    let nextStep = currentStep + 1;
    
    // Skip conditional family steps (6, 7, 8)
    if (nextStep >= 6 && nextStep <= 8 && !shouldShowFamilySteps) {
      nextStep = 9;
    }
    
    // Skip conditional investment steps (9, 10, 11, 12)
    if (nextStep >= 9 && nextStep <= 12 && !shouldShowInvestmentSteps) {
      nextStep = 13;
    }
    
    if (nextStep < steps.length) {
      setCurrentStep(nextStep);
    } else {
      await handleSubmit();
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      let prevStep = currentStep - 1;
      
      // Skip conditional investment steps backwards
      if (prevStep >= 9 && prevStep <= 12 && !shouldShowInvestmentSteps) {
        prevStep = 5;
      }
      
      // Skip conditional family steps backwards  
      if (prevStep >= 6 && prevStep <= 8 && !shouldShowFamilySteps) {
        prevStep = 5;
      }
      
      setCurrentStep(prevStep);
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${API}/buyer-strategy`, formData);
      navigate("/buyer-strategy-result", { 
        state: { strategy: response.data, requiresLeadCapture: true } 
      });
    } catch (error) {
      console.error("Strategy error:", error);
      alert("Error generating strategy. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Loading messages that rotate
  const [loadingMessage, setLoadingMessage] = React.useState(0);
  const loadingMessages = [
    "Analyzing your preferences...",
    "Calculating ROI projections...",
    "Identifying top areas...",
    "Generating personalized strategy...",
    "Almost there..."
  ];

  React.useEffect(() => {
    if (loading) {
      const interval = setInterval(() => {
        setLoadingMessage((prev) => (prev + 1) % loadingMessages.length);
      }, 3000);
      return () => clearInterval(interval);
    }
  }, [loading]);

  const renderStep = () => {
    switch (currentStep) {
      // Step 0: Buying Purpose
      case 0:
        return (
          <div className="space-y-4">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">What is your primary goal?</h2>
            {[
              { value: 'self_use', label: 'Self-Use (Living with family)' },
              { value: 'investment', label: 'Long-Term Investment (3-7 years)' },
              { value: 'rental', label: 'Rental Income Focus' },
              { value: 'flipping', label: 'Short-Term Flipping (6-18 months)' },
              { value: 'pre_launch', label: 'Pre-Launch Entry' }
            ].map(option => (
              <button
                key={option.value}
                onClick={() => setFormData({...formData, buying_purpose: option.value})}
                className={`w-full p-4 text-left border-2 rounded-lg transition ${
                  formData.buying_purpose === option.value
                    ? 'border-brand-500 bg-brand-50'
                    : 'border-gray-200 hover:border-blue-300'
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
        );

      // Step 1: Budget
      case 1:
        return (
          <div>
            <label className="block text-2xl font-bold mb-6">What is your total budget?</label>
            <select
              value={formData.budget}
              onChange={(e) => setFormData({...formData, budget: e.target.value})}
              className="w-full p-4 border-2 border-gray-300 rounded-lg text-lg"
            >
              <option value="">Select Budget</option>
              <option value="under_50l">Under ₹50L</option>
              <option value="50l_80l">₹50L - ₹80L</option>
              <option value="80l_1.2cr">₹80L - ₹1.2Cr</option>
              <option value="1.2cr_2cr">₹1.2Cr - ₹2Cr</option>
              <option value="2cr_plus">₹2Cr+</option>
            </select>
          </div>
        );

      // Step 2: Loan Type
      case 2:
        return (
          <div className="space-y-4">
            <h2 className="text-2xl font-bold mb-6">Will you take a home loan?</h2>
            {[
              { value: 'yes_loan', label: 'Yes (Home Loan)' },
              { value: 'no_cash', label: 'No (Full Cash)' },
              { value: 'part_loan', label: 'Part Loan / Part Cash' }
            ].map(option => (
              <button
                key={option.value}
                onClick={() => setFormData({...formData, loan_type: option.value})}
                className={`w-full p-4 border-2 rounded-lg ${
                  formData.loan_type === option.value
                    ? 'border-brand-500 bg-brand-50'
                    : 'border-gray-200'
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
        );

      // Step 3: Possession Timeline
      case 3:
        return (
          <div className="space-y-4">
            <h2 className="text-2xl font-bold mb-6">When do you want possession?</h2>
            {[
              { value: 'immediate', label: 'Immediate (Ready to Move)' },
              { value: '1_2_years', label: 'Within 1-2 years' },
              { value: '2_4_years', label: '2-4 years' },
              { value: 'flexible', label: 'Flexible if returns are strong' }
            ].map(option => (
              <button
                key={option.value}
                onClick={() => setFormData({...formData, possession_timeline: option.value})}
                className={`w-full p-4 border-2 rounded-lg ${
                  formData.possession_timeline === option.value
                    ? 'border-brand-500 bg-brand-50'
                    : 'border-gray-200'
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
        );

      // Step 4: Holding Period
      case 4:
        return (
          <div className="space-y-4">
            <h2 className="text-2xl font-bold mb-6">How long will you hold the property?</h2>
            {[
              { value: 'less_2_years', label: 'Less than 2 years' },
              { value: '3_5_years', label: '3-5 years' },
              { value: '5_plus_years', label: '5+ years' }
            ].map(option => (
              <button
                key={option.value}
                onClick={() => setFormData({...formData, holding_period: option.value})}
                className={`w-full p-4 border-2 rounded-lg ${
                  formData.holding_period === option.value
                    ? 'border-brand-500 bg-brand-50'
                    : 'border-gray-200'
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
        );

      // Step 5: Risk Appetite
      case 5:
        return (
          <div className="space-y-4">
            <h2 className="text-2xl font-bold mb-6">What best describes you?</h2>
            {[
              { value: 'low', label: 'Low Risk (Safety first)' },
              { value: 'moderate', label: 'Moderate Risk (Balanced)' },
              { value: 'high', label: 'High Risk (High growth focus)' }
            ].map(option => (
              <button
                key={option.value}
                onClick={() => setFormData({...formData, risk_appetite: option.value})}
                className={`w-full p-4 border-2 rounded-lg ${
                  formData.risk_appetite === option.value
                    ? 'border-brand-500 bg-brand-50'
                    : 'border-gray-200'
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
        );

      // Step 6: Family Size (conditional - self_use only)
      case 6:
        if (!shouldShowFamilySteps) return null;
        return (
          <div>
            <label className="block text-2xl font-bold mb-6">What is your family size?</label>
            <input
              type="text"
              value={formData.family_size}
              onChange={(e) => setFormData({...formData, family_size: e.target.value})}
              placeholder="e.g., 4 members"
              className="w-full p-4 border-2 border-gray-300 rounded-lg text-lg"
            />
          </div>
        );

      // Step 7: Required BHK (conditional - self_use only)
      case 7:
        if (!shouldShowFamilySteps) return null;
        return (
          <div>
            <label className="block text-2xl font-bold mb-6">Minimum required BHK?</label>
            <select
              value={formData.required_bhk}
              onChange={(e) => setFormData({...formData, required_bhk: e.target.value})}
              className="w-full p-4 border-2 border-gray-300 rounded-lg text-lg"
            >
              <option value="">Select</option>
              <option value="1 BHK">1 BHK</option>
              <option value="2 BHK">2 BHK</option>
              <option value="3 BHK">3 BHK</option>
              <option value="4 BHK">4 BHK+</option>
            </select>
          </div>
        );

      // Step 8: School/Metro (conditional - self_use only)
      case 8:
        if (!shouldShowFamilySteps) return null;
        return (
          <div className="space-y-4">
            <h2 className="text-2xl font-bold mb-6">Do you need school/metro nearby?</h2>
            {[
              { value: true, label: 'Yes, essential' },
              { value: false, label: 'No, not necessary' }
            ].map(option => (
              <button
                key={String(option.value)}
                onClick={() => setFormData({...formData, need_school_metro: option.value})}
                className={`w-full p-4 border-2 rounded-lg ${
                  formData.need_school_metro === option.value
                    ? 'border-brand-500 bg-brand-50'
                    : 'border-gray-200'
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
        );

      // Step 9: Rental Priority (conditional - investment only)
      case 9:
        if (!shouldShowInvestmentSteps) return null;
        return (
          <div className="space-y-4">
            <h2 className="text-2xl font-bold mb-6">Is rental income a priority?</h2>
            {[
              { value: true, label: 'Yes, rental income is important' },
              { value: false, label: 'No, focus on appreciation' }
            ].map(option => (
              <button
                key={String(option.value)}
                onClick={() => setFormData({...formData, rental_priority: option.value})}
                className={`w-full p-4 border-2 rounded-lg ${
                  formData.rental_priority === option.value
                    ? 'border-brand-500 bg-brand-50'
                    : 'border-gray-200'
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
        );

      // Step 10: Appreciation Priority (conditional - investment only)
      case 10:
        if (!shouldShowInvestmentSteps) return null;
        return (
          <div className="space-y-4">
            <h2 className="text-2xl font-bold mb-6">Is capital appreciation a priority?</h2>
            {[
              { value: true, label: 'Yes, maximize appreciation' },
              { value: false, label: 'Not the main focus' }
            ].map(option => (
              <button
                key={String(option.value)}
                onClick={() => setFormData({...formData, appreciation_priority: option.value})}
                className={`w-full p-4 border-2 rounded-lg ${
                  formData.appreciation_priority === option.value
                    ? 'border-brand-500 bg-brand-50'
                    : 'border-gray-200'
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
        );

      // Step 11: Under Construction (conditional - investment only)
      case 11:
        if (!shouldShowInvestmentSteps) return null;
        return (
          <div className="space-y-4">
            <h2 className="text-2xl font-bold mb-6">Open to under-construction properties?</h2>
            {[
              { value: true, label: 'Yes, open to under-construction' },
              { value: false, label: 'No, only ready properties' }
            ].map(option => (
              <button
                key={String(option.value)}
                onClick={() => setFormData({...formData, open_to_under_construction: option.value})}
                className={`w-full p-4 border-2 rounded-lg ${
                  formData.open_to_under_construction === option.value
                    ? 'border-brand-500 bg-brand-50'
                    : 'border-gray-200'
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
        );

      // Step 12: Pre Launch (conditional - investment only)
      case 12:
        if (!shouldShowInvestmentSteps) return null;
        return (
          <div className="space-y-4">
            <h2 className="text-2xl font-bold mb-6">Open to pre-launch projects?</h2>
            {[
              { value: true, label: 'Yes, for better pricing' },
              { value: false, label: 'No, too risky' }
            ].map(option => (
              <button
                key={String(option.value)}
                onClick={() => setFormData({...formData, open_to_pre_launch: option.value})}
                className={`w-full p-4 border-2 rounded-lg ${
                  formData.open_to_pre_launch === option.value
                    ? 'border-brand-500 bg-brand-50'
                    : 'border-gray-200'
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
        );

      // Step 13: Biggest Concern
      case 13:
        return (
          <div className="space-y-4">
            <h2 className="text-2xl font-bold mb-6">What worries you most?</h2>
            {[
              { value: 'builder_delays', label: 'Builder delays' },
              { value: 'overpaying', label: 'Overpaying' },
              { value: 'wrong_area', label: 'Wrong area selection' },
              { value: 'poor_resale', label: 'Poor resale liquidity' },
              { value: 'legal_issues', label: 'Legal issues' }
            ].map(option => (
              <button
                key={option.value}
                onClick={() => setFormData({...formData, biggest_concern: option.value})}
                className={`w-full p-4 border-2 rounded-lg ${
                  formData.biggest_concern === option.value
                    ? 'border-brand-500 bg-brand-50'
                    : 'border-gray-200'
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
        );

      default:
        return null;
    }
  };

  const progress = ((currentStep + 1) / steps.length) * 100;

  return (
    <div className="min-h-screen bg-gradient-to-br from-brand-500 via-brand-500 to-brand-600 py-12 relative overflow-hidden">
      {/* Background Image */}
      <div 
        className="absolute inset-0 opacity-20"
        style={{
          backgroundImage: "url('https://images.unsplash.com/photo-1600210492493-0946911123ea?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzl8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBhcGFydG1lbnQlMjBpbnRlcmlvcnxlbnwwfHx8fDE3Mzc4ODk4MDB8MA&ixlib=rb-4.1.0&q=85')",
          backgroundSize: "cover",
          backgroundPosition: "center"
        }}
      ></div>

      {/* Loading Modal */}
      {loading && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-8 max-w-md mx-4 text-center">
            <div className="mb-6">
              <div className="inline-block animate-spin rounded-full h-16 w-16 border-b-4 border-brand-500"></div>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-3">
              Creating Your Strategy...
            </h2>
            <p className="text-lg text-brand-500 font-semibold mb-2">
              {loadingMessages[loadingMessage]}
            </p>
            <p className="text-sm text-gray-500">
              ⏱ Strategizing in less than 60 seconds
            </p>
            <div className="mt-4 flex justify-center space-x-1">
              <div className="w-2 h-2 bg-brand-500 rounded-full animate-bounce" style={{animationDelay: '0s'}}></div>
              <div className="w-2 h-2 bg-brand-500 rounded-full animate-bounce" style={{animationDelay: '0.2s'}}></div>
              <div className="w-2 h-2 bg-brand-500 rounded-full animate-bounce" style={{animationDelay: '0.4s'}}></div>
            </div>
          </div>
        </div>
      )}

      <div className="max-w-2xl mx-auto px-4 relative z-10">
        <div className="bg-white rounded-2xl shadow-2xl p-8">
          {/* Headline Section */}
          <div className="text-center mb-8">
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4 leading-tight">
              Make the Right Property Decision Backed by Data, Not Guesswork.
            </h1>
            <p className="text-lg md:text-xl text-gray-600 leading-relaxed">
              Whether you're buying for living, investing, or flipping<br />
              Get a personalised property strategy in 60 seconds.
            </p>
          </div>

          <div className="mb-8">
            <div className="flex justify-between text-sm text-gray-600 mb-2">
              <span>Step {currentStep + 1} of {steps.length}</span>
              <span>{Math.round(progress)}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-brand-500 h-2 rounded-full transition-all duration-300"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>

          {renderStep()}

          <div className="flex gap-4 mt-8">
            {currentStep > 0 && (
              <button
                onClick={handleBack}
                className="flex-1 py-3 border-2 border-gray-300 rounded-lg font-semibold hover:bg-gray-50"
              >
                Back
              </button>
            )}
            <button
              onClick={handleNext}
              disabled={loading}
              className="flex-1 py-3 bg-brand-500 text-white rounded-lg font-semibold hover:bg-brand-600 disabled:opacity-50"
            >
              {loading ? 'Generating...' : currentStep === steps.length - 1 ? 'Get Strategy' : 'Next'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BuyerStrategyPage;
